import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  BookOpen,
  Calendar,
  Clock,
  FileText,
  CreditCard,
  MessageSquare,
  Pencil,
  Check,
  X,
  Loader2,
  Sparkles,
  ExternalLink,
} from 'lucide-react';
import { toast } from '@/store/toastStore';
import { getCommLogByPatient, type CommLogEntry } from '@/services/communications.service';
import { apiClient } from '@/services/apiClient';
import { getPatientContext } from '@/services/ai-context.service';
import { FormattedText } from '@/components/ui/FormattedText';
import {
  getPatientSidebarStats,
  type PatientSidebarStats,
} from '@/services/patients.service';
import {
  appointmentsService,
  type Appointment,
} from '@/services/appointments.service';
import { getPlansByPatient } from '@/services/plans.service';
import { AppointmentDetailModal } from '@/pages/appointments/AppointmentDetailModal';
import {
  invalidatePatientAppointments,
  patientQueryKeys,
  usePatientAppointments,
  usePatientMemberships,
} from '@/hooks/usePatientDetail';
import { useInViewport } from '@/hooks/useInViewport';
import { getPatientProductTypes } from '@/services/patientProductTypes.service';
import { type ProductTypesResponse } from '@/components/patients/PatientProductsDisplay';
import {
  buildApptProductStats,
  getAppointmentActionReasons,
} from '@/lib/appointmentActions';
import { formatApptDateTimeInFacilityTz } from '@/lib/facilityTimezone';
import { Badge } from '@/components/ui';
import { RecurringDetailModal } from '@/pages/memberships/RecurringDetailModal';

// ── Utilities ─────────────────────────────────────────────────────────────────

function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value);
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function membershipAccentColor(section: string, declined: boolean): string {
  if (declined) return '#ef4444';
  switch (section) {
    case 'frozen': return '#3b82f6';
    case 'upcoming': return '#f59e0b';
    case 'cancelling': return '#ef4444';
    default: return '#22c55e';
  }
}

/**
 * Appointment date + time in the *facility's* zone, matching the cards on the
 * Appointments tab. Without the facility zone these cards rendered in the
 * browser's zone, so an Eastern clinic's 3:00 PM read as 12:00 PM to Pacific
 * staff.
 */
export function formatApptDateTime(
  startTime: string,
  facilityTz?: string | null,
): string {
  return formatApptDateTimeInFacilityTz(startTime, facilityTz, ' at ');
}

export function formatApptDateOnly(startTime: string): string {
  const d = new Date(startTime);
  if (isNaN(d.getTime())) return startTime;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getMessageCardColor(type: string): string {
  if (type.includes('sms')) return 'bg-blue-100 border-blue-200';
  if (type.includes('email')) return 'bg-orange-100 border-orange-200';
  if (type.includes('whatsapp')) return 'bg-emerald-100 border-emerald-200';
  if (type.includes('video')) return 'bg-violet-50 border-violet-300';
  if (type.includes('call') || type.includes('phone')) return 'bg-green-50 border-green-300';
  if (type.includes('note')) return 'bg-yellow-50 border-yellow-300';
  return 'bg-slate-50 border-slate-200';
}

/**
 * Convert a (potentially HTML-entity-encoded, sometimes double-encoded) email
 * or SMS body into a clean preview string.
 */
function htmlToPlainPreview(input: string, maxLen = 80): string {
  if (!input) return '';

  const parser = new DOMParser();
  let text = parser.parseFromString(input, 'text/html').body.textContent ?? '';

  if (/<[a-z!/]/i.test(text)) {
    text = parser.parseFromString(text, 'text/html').body.textContent ?? '';
  }

  text = text.replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();

  return text.length > maxLen ? text.slice(0, maxLen) + '…' : text;
}

function useLazyQueryEnabled(active: boolean): boolean {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!active) {
      setEnabled(false);
      return;
    }

    setEnabled(false);

    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (typeof idleWindow.requestIdleCallback === 'function') {
      const handle = idleWindow.requestIdleCallback(() => setEnabled(true), { timeout: 800 });
      return () => idleWindow.cancelIdleCallback?.(handle);
    }

    const handle = window.setTimeout(() => setEnabled(true), 150);
    return () => window.clearTimeout(handle);
  }, [active]);

  return active && enabled;
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatClientSince(value: string | null): string {
  if (!value || value.startsWith('0000')) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function formatLastAppointment(_type: string | null | undefined, dateStr: string | null | undefined): string {
  if (!dateStr || dateStr.startsWith('0000')) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Shared Components ─────────────────────────────────────────────────────────

/**
 * Empty-state text for the dashboard sections.
 *
 * A populated section renders a stack of padded, bordered rows; the empty state
 * was a bare inline span, so on the profile page a section with no rows
 * collapsed to header height and read as a broken card next to its neighbours.
 * Below md it becomes a block with a row's padding and a dashed border, which
 * is also what makes the equal-height grid on that page look deliberate rather
 * than like a rendering fault. At md and above it stays the inline span it has
 * always been, so no desktop surface changes.
 */
const EMPTY_STATE_CLASS =
  'text-xs text-slate-500 max-md:block max-md:rounded-lg max-md:border ' +
  'max-md:border-dashed max-md:border-slate-200 max-md:bg-slate-50 max-md:p-3';

export function SectionHeader({ icon: Icon, label, onNavigate }: { icon: typeof Calendar; label: string; onNavigate?: () => void }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Icon className="h-4 w-4 text-slate-500" />
        {label}
      </h4>
      {onNavigate && (
        <button
          type="button"
          onClick={onNavigate}
          className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          title={`Go to ${label}`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function PatientStatCard({
  label,
  value,
  loading,
}: {
  label: string;
  value: string;
  loading?: boolean;
}) {
  return (
    // min-w-0 below md: the value is `truncate`, so without it this card's
    // automatic minimum size is the whole value string and the card overflows
    // its own grid track at phone widths.
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 max-md:min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      {loading ? (
        <div className="mt-1 h-5 w-16 animate-pulse rounded bg-slate-200" />
      ) : (
        <p className="mt-0.5 truncate text-sm font-semibold text-slate-900">{value}</p>
      )}
    </div>
  );
}

// ── Section Components ────────────────────────────────────────────────────────

export function PatientQuickStatsSection({
  patientId,
  seedTotalSpend,
  showExtended,
}: {
  patientId: number | null;
  seedTotalSpend?: number | null;
  showExtended?: boolean;
}) {
  const queryEnabled = useLazyQueryEnabled(!!patientId);
  const { data, isError } = useQuery({
    queryKey: ['patient-sidebar-stats', patientId],
    queryFn: () => getPatientSidebarStats(patientId!),
    enabled: queryEnabled,
    staleTime: 60_000,
  });

  if (!patientId || isError) return null;

  const stats: PatientSidebarStats | null = data ?? null;
  const loading = !stats;
  const totalSpendValue = stats
    ? formatMoney(stats.totalSpend)
    : typeof seedTotalSpend === 'number'
      ? formatMoney(seedTotalSpend)
      : '';

  const compedDisplay = stats
    ? `${stats.compedTotal ?? 0} / ${formatMoney(stats.compedValue ?? 0)}`
    : '';

  const lastApptDisplay = stats
    ? formatLastAppointment(stats.lastAppointmentType, stats.lastAppointmentDate)
    : '';

  return (
    // Both column counts are breakpoint-prefixed so the lint rule sees no bare
    // grid and this file can come off the burndown. The md: values are the
    // counts this rendered before — 3 extended, 2 not — so every desktop width
    // is unchanged. Only below md does the extended variant drop 3 to 2, which
    // is what stops three truncating stat cards sharing a 375px row.
    <div className={showExtended ? 'grid gap-2 max-md:grid-cols-2 md:grid-cols-3' : 'grid gap-2 max-md:grid-cols-2 md:grid-cols-2'}>
      <PatientStatCard
        label="Total Spend"
        value={totalSpendValue}
        loading={loading && !totalSpendValue}
      />
      {showExtended && (
        <PatientStatCard
          label="Comped"
          value={compedDisplay}
          loading={loading}
        />
      )}
      <PatientStatCard
        label="Client Since"
        value={stats ? formatClientSince(stats.clientSince) : ''}
        loading={loading}
      />
      <PatientStatCard
        label="Appointments"
        value={stats ? String(stats.appointmentCount) : ''}
        loading={loading}
      />
      <PatientStatCard
        label="No-Shows"
        value={stats ? String(stats.noShowCount) : ''}
        loading={loading}
      />
      {showExtended && (
        <PatientStatCard
          label="Last Appointment"
          value={lastApptDisplay}
          loading={loading}
        />
      )}
    </div>
  );
}

export function PatientAiSummarySection({ patientId }: { patientId: number | null }) {
  const queryEnabled = useLazyQueryEnabled(!!patientId);
  const { data, isFetching, isError } = useQuery({
    queryKey: ['ai-context', 'patient', patientId],
    queryFn: () => getPatientContext(patientId!),
    enabled: queryEnabled,
    staleTime: 60_000,
  });

  const aiSummary = data?.aiSummary?.trim() ?? '';

  if (!patientId || isError) return null;

  if (!aiSummary && isFetching) {
    return (
      <div>
        <SectionHeader icon={Sparkles} label="AI Summary" />
        <div className="rounded-lg border border-purple-100 bg-purple-50 p-3 text-xs text-purple-700">
          Loading AI summary...
        </div>
      </div>
    );
  }

  if (!aiSummary) return null;

  return (
    <div>
      <SectionHeader icon={Sparkles} label="AI Summary" />
      <FormattedText
        text={aiSummary}
        className="rounded-lg bg-purple-50 border border-purple-200 p-3 text-sm text-purple-900 leading-relaxed break-words"
      />
    </div>
  );
}

export function EditableNoteSection({ note, patientId }: { note: string; patientId: number | null }) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note);
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = useCallback(() => { setDraft(note); setIsEditing(true); }, [note]);
  const handleCancel = useCallback(() => { setIsEditing(false); setDraft(note); }, [note]);
  const handleSave = useCallback(async () => {
    if (!patientId) return;
    setIsSaving(true);
    try {
      await apiClient.put(`/patients/${patientId}/primarynote`, { note: draft });
      await queryClient.invalidateQueries({
        queryKey: patientQueryKeys.note(String(patientId)),
      });
      setIsEditing(false);
    } catch { /* keep edit mode */ } finally { setIsSaving(false); }
  }, [patientId, draft, queryClient]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <FileText className="h-4 w-4 text-slate-500" />
          Note
        </h4>
        {!isEditing && patientId && (
          <button onClick={handleEdit} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title="Edit note">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {isEditing ? (
        <div className="space-y-2">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-slate-800 leading-relaxed resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent" rows={4} autoFocus />
          <div className="flex items-center justify-end gap-1.5">
            <button onClick={handleCancel} disabled={isSaving} className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-50">
              <X className="h-3 w-3" /> Cancel
            </button>
            <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium text-white bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50">
              {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
            </button>
          </div>
        </div>
      ) : note ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap break-words">{note}</div>
      ) : (
        <button onClick={handleEdit} className="text-xs text-primary hover:underline cursor-pointer">+ Add a note</button>
      )}
    </div>
  );
}

/** Plans shown in the sidebar before collapsing the remainder into "+N more". */
const PLANS_PREVIEW_LIMIT = 5;

export function PlansSection({
  patientId,
  onSectionNavigate,
}: {
  patientId: number | null;
  onSectionNavigate?: (section: string) => void;
}) {
  const { ref, inView } = useInViewport<HTMLDivElement>({ skip: !patientId });
  // Deliberately the same query key (and numeric id) as PlansTab and the
  // patient detail page's prefetch, so the sidebar and the Plans tab share one
  // cache entry rather than fetching the patient's plans twice.
  const { data, isLoading } = useQuery({
    queryKey: ['patient-plans', patientId],
    queryFn: () => getPlansByPatient(patientId!),
    enabled: inView && !!patientId,
    staleTime: 60_000,
  });
  const plans = data ?? [];
  const visiblePlans = plans.slice(0, PLANS_PREVIEW_LIMIT);
  const hiddenCount = plans.length - visiblePlans.length;

  return (
    <div ref={ref}>
      <SectionHeader icon={BookOpen} label="Plans" onNavigate={onSectionNavigate ? () => onSectionNavigate('plans') : undefined} />
      {!inView || isLoading ? (
        <div className="h-14 rounded-lg bg-slate-100 animate-pulse" />
      ) : visiblePlans.length > 0 ? (
        <div className="space-y-2">
          {visiblePlans.map((plan) => {
            const stage = [plan.stageName, plan.stageSubName]
              .map((part) => part?.trim())
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={plan.planId}
                className="rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer transition-colors hover:bg-slate-100"
                onClick={() => onSectionNavigate?.('plans')}
              >
                <p className="text-sm font-medium text-slate-800 truncate">
                  {plan.planTypeName?.trim() || 'No Plan Type'}
                </p>
                {stage && <p className="mt-1 text-xs text-slate-500 truncate">{stage}</p>}
              </div>
            );
          })}
          {hiddenCount > 0 && (
            <button
              type="button"
              className="text-xs font-medium text-slate-500 transition-colors hover:text-slate-700"
              onClick={() => onSectionNavigate?.('plans')}
            >
              +{hiddenCount} more
            </button>
          )}
        </div>
      ) : (
        <span className="text-xs text-slate-500">No plans</span>
      )}
    </div>
  );
}

export function MembershipsSection({
  patientId,
  onSectionNavigate,
}: {
  patientId: number | null;
  onSectionNavigate?: (section: string) => void;
}) {
  const { ref, inView } = useInViewport<HTMLDivElement>({ skip: !patientId });
  const queryClient = useQueryClient();
  const { data: memberships, isLoading } = usePatientMemberships(
    inView && patientId ? String(patientId) : null,
  );
  const [editRecurringId, setEditRecurringId] = useState<number | null>(null);

  const handleModalChanged = useCallback(() => {
    if (patientId) {
      void queryClient.invalidateQueries({ queryKey: patientQueryKeys.memberships(String(patientId)) });
    }
  }, [patientId, queryClient]);

  return (
    <div ref={ref}>
      <SectionHeader icon={CreditCard} label="Membership" onNavigate={onSectionNavigate ? () => onSectionNavigate('memberships') : undefined} />
      {!inView || isLoading ? (
        <div className="space-y-2">
          <div className="h-14 rounded-lg bg-slate-100 animate-pulse" />
        </div>
      ) : memberships.length > 0 ? (
        <div className="space-y-2">
          {memberships.map((m, i) => (
            <button
              type="button"
              key={m.recurringId || i}
              className="relative flex w-full gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-slate-300 hover:shadow"
              style={{ borderLeftWidth: 4, borderLeftColor: membershipAccentColor(m.section, m.declined) }}
              onClick={() => {
                if (m.recurringId) {
                  setEditRecurringId(m.recurringId);
                } else {
                  onSectionNavigate?.('memberships');
                }
              }}
            >
              <div className="absolute top-3 right-3 flex items-center gap-1.5">
                {m.declined && <Badge variant="error" size="sm">Declined</Badge>}
                {m.section === 'upcoming'
                  ? <Badge variant="warning" size="sm">Upcoming</Badge>
                  : m.section === 'frozen'
                    ? <Badge variant="info" size="sm">Frozen</Badge>
                    : m.section === 'cancelling'
                      ? <Badge variant="error" size="sm">Set to Cancel</Badge>
                      : <Badge variant="success" size="sm">Active</Badge>}
              </div>

              <div className="flex-1 min-w-0 space-y-1.5">
                <p className="text-sm font-semibold text-slate-800 truncate pr-24">
                  {m.title || 'Membership'}
                </p>

                <div className="text-xs text-slate-500 space-y-0.5">
                  <div>
                    Start Date: <span className="font-medium text-slate-700">{m.firstBillingDate ? formatShortDate(m.firstBillingDate) : '—'}</span>
                  </div>

                  {m.section === 'cancelling' && (
                    <div>
                      Cancels: <span className="font-medium text-slate-700">{m.paymentThroughDate ? formatShortDate(m.paymentThroughDate) : '—'}</span>
                    </div>
                  )}

                  {(m.section === 'active' || m.section === 'frozen') && (
                    <div className="flex items-center gap-4 flex-wrap">
                      <span>
                        Next Billing: <span className="font-medium text-slate-700">{m.nextBillingDate ? formatShortDate(m.nextBillingDate) : '—'}</span>
                      </span>
                      <span>
                        Paid Through: <span className="font-medium text-slate-700">{m.paymentThroughDate ? formatShortDate(m.paymentThroughDate) : '—'}</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <span className={EMPTY_STATE_CLASS}>No memberships</span>
      )}

      <RecurringDetailModal
        recurringId={editRecurringId}
        open={editRecurringId !== null}
        onClose={() => setEditRecurringId(null)}
        onChanged={handleModalChanged}
      />
    </div>
  );
}

export function UpcomingAppointmentsSection({
  patientId,
  onSectionNavigate,
}: {
  patientId: number | null;
  onSectionNavigate?: (section: string) => void;
}) {
  const { ref, inView } = useInViewport<HTMLDivElement>({ skip: !patientId });
  const queryClient = useQueryClient();
  const { data, isLoading } = usePatientAppointments(
    inView && patientId ? String(patientId) : null,
  );
  const appointments = data?.upcomingAppointments ?? [];

  const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(null);
  const [, setLoadingApptId] = useState<number | null>(null);

  const handleAppointmentChanged = useCallback(() => {
    if (patientId) invalidatePatientAppointments(queryClient, patientId);
  }, [patientId, queryClient]);

  const openAppointment = useCallback(async (appointmentId: number) => {
    setLoadingApptId(appointmentId);
    try {
      const fullAppointment = await appointmentsService.getAppointment(appointmentId);
      setDetailAppointment(fullAppointment);
    } catch {
      toast.error('Failed to load appointment details.');
    } finally {
      setLoadingApptId(null);
    }
  }, []);

  return (
    <div ref={ref}>
      <SectionHeader icon={Calendar} label="Upcoming Appointments" onNavigate={onSectionNavigate ? () => onSectionNavigate('appointments') : undefined} />
      {!inView || isLoading ? (
        <div className="h-14 rounded-lg bg-slate-100 animate-pulse" />
      ) : appointments.length > 0 ? (
        <div className="space-y-2">
          {appointments.map((a, i) => (
            <div
              key={i}
              className="rounded-lg border border-blue-200 bg-blue-50 p-3 cursor-pointer transition-colors hover:bg-blue-100"
              onClick={() => a.appointmentId ? openAppointment(a.appointmentId) : onSectionNavigate?.('appointments')}
            >
              <p className="text-sm font-medium text-slate-800 max-md:break-words">{a.title}</p>
              <p className="mt-1 text-xs text-slate-500">{formatApptDateTime(a.startTime, a.facilityIanaTimezone)}</p>
              {a.facilityName && <p className="text-xs text-slate-500">{a.facilityName}</p>}
            </div>
          ))}
        </div>
      ) : (
        <span className={EMPTY_STATE_CLASS}>No upcoming appointments</span>
      )}

      {detailAppointment && (
        <AppointmentDetailModal
          isOpen={Boolean(detailAppointment)}
          // Also refresh on close, not just on `onUpdated`. The unified modal
          // has save paths that leave it open (the plan-stage prompt) and
          // side-actions (check-in, cancel, patient swap) that report through
          // other callbacks, so closing is the one event guaranteed to follow
          // any edit. Re-invalidating an already-fresh list is a no-op.
          onClose={() => {
            setDetailAppointment(null);
            handleAppointmentChanged();
          }}
          appointment={detailAppointment}
          onUpdated={(updated) => {
            setDetailAppointment(updated);
            handleAppointmentChanged();
          }}
          onDeleted={() => {
            setDetailAppointment(null);
            handleAppointmentChanged();
          }}
        />
      )}
    </div>
  );
}

export function RecentVisitsSection({
  patientId,
  onSectionNavigate,
}: {
  patientId: number | null;
  onSectionNavigate?: (section: string) => void;
}) {
  const { ref, inView } = useInViewport<HTMLDivElement>({ skip: !patientId });
  const queryClient = useQueryClient();
  const { data, isLoading } = usePatientAppointments(
    inView && patientId ? String(patientId) : null,
  );
  const appointments = data?.recentAppointments ?? [];

  // Same query key the Appointments tab uses, so the two views share one fetch
  // and one invalidation (invalidatePatientAppointments covers it).
  const { data: productData } = useQuery({
    queryKey: ['patient-appointments-products', patientId],
    queryFn: () => getPatientProductTypes(String(patientId)),
    enabled: Boolean(inView && patientId),
    staleTime: 60_000,
  });

  const apptStatsMap = useMemo(
    () => buildApptProductStats(productData as ProductTypesResponse | undefined),
    [productData],
  );

  const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(null);
  const [, setLoadingApptId] = useState<number | null>(null);

  const handleAppointmentChanged = useCallback(() => {
    if (patientId) invalidatePatientAppointments(queryClient, patientId);
  }, [patientId, queryClient]);

  const openAppointment = useCallback(async (appointmentId: number) => {
    setLoadingApptId(appointmentId);
    try {
      const fullAppointment = await appointmentsService.getAppointment(appointmentId);
      setDetailAppointment(fullAppointment);
    } catch {
      toast.error('Failed to load appointment details.');
    } finally {
      setLoadingApptId(null);
    }
  }, []);

  return (
    <div ref={ref}>
      <SectionHeader icon={Clock} label="Recent Visits" onNavigate={onSectionNavigate ? () => onSectionNavigate('appointments') : undefined} />
      {!inView || isLoading ? (
        <div className="h-14 rounded-lg bg-slate-100 animate-pulse" />
      ) : appointments.length > 0 ? (
        <div className="space-y-2">
          {appointments.map((a, i) => {
            // Recent Visits only ever holds past appointments, which is what
            // makes the "not checked out" half of this rule meaningful.
            const actionReasons = getAppointmentActionReasons(
              { checkedOutAt: a.checkedOutAt, noShow: a.noShow },
              a.appointmentId ? apptStatsMap.get(a.appointmentId) : undefined,
            );
            return (
              <div
                key={i}
                className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                  actionReasons.length > 0
                    ? 'border-amber-200 bg-amber-50 hover:bg-amber-100'
                    : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                }`}
                onClick={() => a.appointmentId ? openAppointment(a.appointmentId) : onSectionNavigate?.('appointments')}
              >
                <p className="text-sm font-medium text-slate-800 max-md:break-words">{a.title}</p>
                <p className="mt-1 text-xs text-slate-500">{formatApptDateTime(a.startTime, a.facilityIanaTimezone)}</p>
                {a.facilityName && <p className="text-xs text-slate-500">{a.facilityName}</p>}
                {actionReasons.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1">
                    {actionReasons.map((reason) => (
                      <span
                        key={reason}
                        className="rounded-full border border-amber-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-amber-700"
                      >
                        {reason}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <span className={EMPTY_STATE_CLASS}>No recent visits</span>
      )}

      {detailAppointment && (
        <AppointmentDetailModal
          isOpen={Boolean(detailAppointment)}
          // Also refresh on close, not just on `onUpdated`. The unified modal
          // has save paths that leave it open (the plan-stage prompt) and
          // side-actions (check-in, cancel, patient swap) that report through
          // other callbacks, so closing is the one event guaranteed to follow
          // any edit. Re-invalidating an already-fresh list is a no-op.
          onClose={() => {
            setDetailAppointment(null);
            handleAppointmentChanged();
          }}
          appointment={detailAppointment}
          onUpdated={(updated) => {
            setDetailAppointment(updated);
            handleAppointmentChanged();
          }}
          onDeleted={() => {
            setDetailAppointment(null);
            handleAppointmentChanged();
          }}
        />
      )}
    </div>
  );
}

export function RecentMessagesSection({
  patientId,
  onSectionNavigate,
}: {
  patientId: number | null;
  onSectionNavigate?: (section: string) => void;
}) {
  const { ref, inView } = useInViewport<HTMLDivElement>({ skip: !patientId });
  const { data, isLoading } = useQuery({
    queryKey: ['patient-recent-messages', patientId],
    queryFn: async () => {
      const [commRes, streamsRes] = await Promise.allSettled([
        getCommLogByPatient(patientId!, 1),
        apiClient.get(`/communications/comm-log/patient/${patientId}/streams`).then((r) => r.data),
      ]);

      const commEntries: CommLogEntry[] =
        commRes.status === 'fulfilled' ? commRes.value?.data ?? [] : [];
      const streams = streamsRes.status === 'fulfilled' ? streamsRes.value?.data : null;

      const voiceCalls: CommLogEntry[] = (Array.isArray(streams?.voiceCalls) ? streams.voiceCalls : []).map(
        (vc: Record<string, unknown>) =>
          ({
            commLogId: Number(vc.callStreamsID ?? 0),
            accountId: 0,
            planId: null,
            patientId: Number(vc.patientID ?? patientId ?? 0),
            patientFirstName: null,
            patientLastName: null,
            userId: null,
            userFirstName: null,
            userLastName: null,
            type: 'call_logged' as const,
            subject: `Voice Call (${vc.duration ?? '0'}s)`,
            message: String(vc.summary ?? `Call from ${vc.callFrom ?? ''} to ${vc.callTo ?? ''}`),
            externalRef: null,
            deliveryStatus: null,
            createdAt: String(vc.createDate ?? ''),
            isDeleted: false,
          }) as unknown as CommLogEntry,
      );

      const videoCalls: CommLogEntry[] = (Array.isArray(streams?.videoCalls) ? streams.videoCalls : []).map(
        (vc: Record<string, unknown>) =>
          ({
            commLogId: Number(vc.videoCallID ?? 0),
            accountId: 0,
            planId: null,
            patientId: Number(vc.patientID ?? patientId ?? 0),
            patientFirstName: null,
            patientLastName: null,
            userId: null,
            userFirstName: null,
            userLastName: null,
            type: 'video' as const,
            subject: `Video Call (${vc.durationFormatted ?? vc.duration ?? '0'}s)`,
            message: vc.recordingMessage ? String(vc.recordingMessage) : 'Video consultation',
            externalRef: null,
            deliveryStatus: null,
            createdAt: String(vc.createDate ?? ''),
            isDeleted: false,
          }) as unknown as CommLogEntry,
      );

      const all = [...commEntries, ...voiceCalls, ...videoCalls];
      all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      return all.slice(0, 5);
    },
    enabled: inView && !!patientId,
    staleTime: 60_000,
  });
  const recentMessages = data ?? [];

  return (
    <div ref={ref}>
      <SectionHeader icon={MessageSquare} label="Recent Messages" />
      {!inView || isLoading ? (
        <div className="h-14 rounded-lg bg-slate-100 animate-pulse" />
      ) : recentMessages.length > 0 ? (
        <div className="space-y-2">
          {recentMessages.map((msg) => {
            const isInbound = msg.type?.includes('received') || msg.fromPatient;
            const senderFirst = isInbound ? msg.patientFirstName : msg.authorFirstName ?? msg.userFirstName;
            const senderLast = isInbound ? msg.patientLastName : msg.authorLastName ?? msg.userLastName;
            const senderName =
              [senderFirst, senderLast].filter(Boolean).join(' ').trim() ||
              (isInbound ? 'Patient' : 'Staff');
            const dateStr = msg.createdAt
              ? new Date(msg.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : '';
            const subject = htmlToPlainPreview(msg.subject ?? '', 120).replace(
              /^SMS\s+to\s+\S+/i,
              'SMS',
            );
            const bodyPreview =
              htmlToPlainPreview(msg.smsMessage || msg.emailBody || msg.message || '') ||
              '(no content)';
            const cardColor = getMessageCardColor(msg.type ?? '');

            return (
              <div
                key={msg.commLogId}
                className={`rounded-lg border p-2.5 overflow-hidden min-w-0 cursor-pointer transition-colors hover:brightness-95 ${cardColor}`}
                onClick={() => onSectionNavigate?.('commlog')}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-slate-700 truncate">{senderName}</span>
                  <span className="text-[11px] text-slate-400 shrink-0">{dateStr}</span>
                </div>
                {subject && (
                  <p className="text-sm text-slate-600 truncate mt-0.5">{subject}</p>
                )}
                <p className="text-sm font-medium text-slate-800 truncate mt-0.5">
                  {bodyPreview || '(no content)'}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <span className={EMPTY_STATE_CLASS}>No recent messages</span>
      )}
    </div>
  );
}
